//@flow
import React from 'react';
import {Link, useLocation, matchPath} from 'react-router-dom';
import {
  makeStyles,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
} from '@material-ui/core';

import {ExpandLess, ExpandMore} from '@material-ui/icons';

import {colors} from '../../../assets/styles/colors';
import TooltipItem from './TooltipItem/TooltipItem';
import CustomChip from '../../../elements/CustomChip';
import UpgradeIcon from '../../../elements/icons/UpgradeIcon';
import {useItemVisibility, useItemNeedsUpSell} from '../hooks/useNavigation';

import {useSidebarSectionContext} from './SidebarSection';
import {useSidebarContext} from './SidebarContainer';
import {classNames} from '../../../utils/react';

import type {NavigationItemConfig, TooltipLink} from '../types';
import {ICONS} from '../config';
import {getCustomLabel, getCustomColors} from './CustomLabels';

type Props = {
  item: NavigationItemConfig,
  level?: number,
  onClick?: () => void,
};

const useStyles = makeStyles(theme => ({
  sidebar: {
    width: '100%',
    border: 'none',
  },
  sidebarPaper: {
    width: '100%',
    boxShadow: '4px 0px 12px rgba(222, 222, 222, 0.25)',
  },
  sidebarList: {
    padding: 0,
    width: '100%',
  },
  sidebarItem: {
    padding: '0 .5rem',
    borderRadius: '.5rem',
    '&:hover': {
      background: 'none',
    },
  },
  sidebarItemDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  sidebarLogo: {
    paddingLeft: 16,
    height: 72,
    '& img': {
      margin: '0 auto',
    },
  },
  sidebarButton: {
    height: 56,
    width: '100%',
    textTransform: 'unset',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(2, 0, 2, 2),
    borderRadius: '1rem',
    cursor: 'pointer',
    color: 'grey',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: 'rgba(133, 133, 133, 0.03)',
      textDecoration: 'none',
    },
  },
  sidebarButtonActive: {
    textDecoration: 'none',
    // backgroundColor: colors.grey200,
    color: 'black',
    '& svg': {
      color: 'black',
    },
  },
  sidebarIconActive: {
    color: 'black',
    '& svg': {
      color: 'black',
    },
  },
  siderbarShrunkItem: {
    padding: theme.spacing(2),
  },
  sidebarItemText: {
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'space-between',
    '& span': {
      fontSize: 14,
    },
  },
  sidebarItemTextActive: {
    fontWeight: 600,
  },
  sidebarItemIcon: {
    textAlign: 'center',
    minWidth: 40,
  },
  sidebarShrunkItemIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  nestedItemLink: {
    textDecoration: 'none',
    width: '100%',
    color: 'grey',
    padding: '8px 0 8px 0',
  },
  nestedItemLinkText: {
    paddingLeft: 48,
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,
  },
  nestedItemLinkTextActive: {
    fontWeight: 500,
    color: 'black',
  },
  nestedItemActiveDot: {
    width: 5,
    height: 5,
    backgroundColor: 'black',
    borderRadius: '100%',
    marginRight: 10,
  },
  shrinkButton: {
    maxWidth: '100%',
    minWidth: 'unset',
  },
  tooltip: {
    background: '#FFFFFF',
    boxShadow: '0px 4px 12px rgba(14, 21, 58, 0.08)',
    borderRadius: '4px',
    zIndex: 99999,
    color: colors.grey800,
    pointerEvents: 'none',
  },
  arrow: {
    color: '#FFFFFF',
  },
  collapseContainer: {
    padding: '.5rem .5rem 0 1.5rem',
  },
  nestedLevel: {
    height: 48,
  },
}));

function getBadgeForItem(
  item: NavigationItemConfig,
  needsUpsell: boolean
): React$Node | null {
  if (needsUpsell) {
    return (
      <CustomChip
        label={
          <>
            Mejorar <UpgradeIcon width={18} height={18} />
          </>
        }
        size="small"
        color="primary"
      />
    );
  }

  if (item.badge) {
    const {untilISO, ...badgeProps} = item.badge;
    if (untilISO) {
      const now = new Date();
      const expiryDate = new Date(untilISO);
      if (now > expiryDate) {
        return null;
      }
    }

    const badgeLabel =
      item.badge.iconKey && ICONS[item.badge.iconKey]
        ? `${item.badge.label || ''}`
        : item.badge.label;

    return (
      <CustomChip
        color="primary"
        size="small"
        {...badgeProps}
        label={badgeLabel}
      />
    );
  }

  return null;
}

function getTooltipItems(item: NavigationItemConfig): TooltipLink[] | null {
  if (item.tooltipItems) {
    return item.tooltipItems.filter(tooltip => !tooltip.hide);
  }

  if (item.children) {
    return item.children
      .filter(child => !child.hide && child.path)
      .map(child => ({
        name: child.label,
        path: child.path || '',
      }));
  }

  return null;
}

export default function SidebarItem(props: Props) {
  const {item, level = 0, onClick} = props;

  const {shrink} = useSidebarContext();
  const sidebarSectionContext = useSidebarSectionContext();
  const classes = useStyles();
  const location = useLocation();

  // Use local state for nested items (level > 0), global context for top-level items
  const [localOpenItem, setLocalOpenItem] = React.useState('');

  const isTopLevel = level === 0;
  const openItem = isTopLevel ? sidebarSectionContext.openItem : localOpenItem;

  const toggleLocalOpenItem = (value: string) => {
    setLocalOpenItem(localOpenItem === value ? '' : value);
  };

  const setOpenItem = isTopLevel
    ? sidebarSectionContext.setOpenItem
    : toggleLocalOpenItem;

  const isVisible = useItemVisibility(item);
  const needsUpsell = useItemNeedsUpSell(item);

  if (!isVisible) {
    return null;
  }

  const icon = item.iconKey ? ICONS[item.iconKey] : null;
  const badge = getBadgeForItem(item, needsUpsell);
  const tooltipItems = getTooltipItems(item);
  const path = item.path || '';
  const active = !!matchPath(location.pathname, {
    path,
    exact: true,
    strict: false,
  });
  const labelComponent = getCustomLabel(item.id, item.label);
  const customColors = getCustomColors(item.id);

  const itemTextClasses = classNames(classes.sidebarItemText, {
    [classes.sidebarItemTextActive]: active,
  });

  const itemClasses = classNames(classes.sidebarButton, {
    [classes.sidebarButtonActive]: active,
    [classes.nestedLevel]: level > 0,
  });

  // Custom styles for items with custom colors
  const customItemStyle = customColors
    ? {
        backgroundColor: customColors.backgroundColor,
        borderRadius: customColors.borderRadius,
        '&:hover': {
          backgroundColor: customColors.backgroundColor,
        },
      }
    : {};

  const open = openItem === path;

  const children = item.children
    ? item.children
        .map(childItem => (
          <SidebarItem key={childItem.id} item={childItem} level={level + 1} />
        ))
        .filter(Boolean)
    : null;

  if (shrink) {
    return (
      <Tooltip
        arrow
        title={tooltipItems ? <TooltipItem items={tooltipItems} /> : item.label}
        placement="right"
        interactive
        classes={{
          tooltip: classes.tooltip,
          arrow: classes.arrow,
        }}
      >
        <ListItem
          button
          className={`${classes.sidebarItem} ${classes.siderbarShrunkItem} ${
            active ? classes.sidebarIconActive : ''
          } ${classes.shrinkButton}`}
        >
          <ListItemIcon
            className={`${classes.sidebarItemIcon} ${classes.sidebarShrunkItemIcon}`}
          >
            {icon}
          </ListItemIcon>
        </ListItem>
      </Tooltip>
    );
  }

  if (children && children.length > 0) {
    const collapseContainerStyle = customColors
      ? {
          backgroundColor: customColors.collapseBackground,
          borderRadius: customColors.collapseBorderRadius,
        }
      : {};

    return (
      <>
        <ListItem
          button
          className={`${classes.sidebarItem} ${itemClasses}`}
          style={customItemStyle}
          onClick={() => {
            setOpenItem(path);
          }}
        >
          <ListItemIcon className={classes.sidebarItemIcon}>
            {icon}
          </ListItemIcon>
          <ListItemText
            disableTypography
            className={`${classes.sidebarItemText} ${
              active ? classes.sidebarItemTextActive : ''
            }`}
          >
            {labelComponent}
            {badge}
          </ListItemText>
          {open ? (
            <ExpandLess style={{marginRight: 12}} />
          ) : (
            <ExpandMore style={{marginRight: 12}} />
          )}
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <div
            className={classes.collapseContainer}
            style={collapseContainerStyle}
          >
            {children}
          </div>
        </Collapse>
      </>
    );
  }

  if (!item.path && !onClick) {
    return (
      <ListItem
        className={`${classes.sidebarItem} ${itemClasses}`}
        style={customItemStyle}
      >
        <ListItemText
          disableTypography
          style={{fontWeight: 400}}
          className={itemTextClasses}
        >
          {labelComponent}
          {badge}
        </ListItemText>
      </ListItem>
    );
  }

  const listItemClasses = classNames(classes.sidebarItem, itemClasses, {
    [classes.sidebarItemActive]: active,
  });

  return (
    <ListItem
      button
      className={listItemClasses}
      style={customItemStyle}
      component={Link}
      to={path}
      target={item.target}
    >
      {icon && (
        <ListItemIcon className={classes.sidebarItemIcon}>{icon}</ListItemIcon>
      )}
      <ListItemText disableTypography className={itemTextClasses}>
        {labelComponent}
        {badge}
      </ListItemText>
    </ListItem>
  );
}
